import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { CatalogIcon } from '@/shared/config';
import { categoriesApi } from '@/shared/api/categories';
import type { Category, Subcategory } from '@/entities/category';
import { useFavorites } from '@/features/favorites';
import { CatalogTile, CategoryGlyph } from '@/widgets/CatalogTile';
import { Button, EmptyState, PageMotion, SearchInput, Skeleton, staggerItem } from '@/shared/ui';
import { pluralRu } from '@/shared/lib/plural';
import { subcategoryPath } from '@/shared/lib/routes';
import styles from './Categories.module.css';

const gridStagger = {
  animate: { transition: { staggerChildren: 0.015 } },
};

function subcategoriesOf(cat: Category): Subcategory[] {
  return (
    cat.Subcategories ??
    (cat as unknown as { subcategories?: Subcategory[] }).subcategories ??
    []
  );
}

function subcategoriesLabel(cat: Category): string {
  const n = subcategoriesOf(cat).length;
  return `${n} ${pluralRu(n, 'подкатегория', 'подкатегории', 'подкатегорий')}`;
}

const Highlight: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className={styles.mark}>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

const POPULAR_LIMIT = 6;

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    categoriesApi
      .list()
      .then((res) => {
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : (raw as { data?: Category[] })?.data ?? [];
        setCategories(list);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  const matchedCategories = useMemo(() => {
    if (!searching) return [];
    return categories.filter((cat) => cat.name.toLowerCase().includes(q));
  }, [categories, q, searching]);

  const matchedSubcategories = useMemo(() => {
    if (!searching) return [];
    const out: { sub: Subcategory; cat: Category }[] = [];
    for (const cat of categories) {
      for (const sub of subcategoriesOf(cat)) {
        if (sub.name.toLowerCase().includes(q)) out.push({ sub, cat });
      }
    }
    return out;
  }, [categories, q, searching]);

  const nothingFound = searching && matchedCategories.length === 0 && matchedSubcategories.length === 0;

  const popular = useMemo(() => {
    return [...categories]
      .filter((cat) => cat.isFeatured || (cat.ticketCount ?? 0) > 0)
      .sort((a, b) => {
        if (Boolean(a.isFeatured) !== Boolean(b.isFeatured)) return a.isFeatured ? -1 : 1;
        return (b.ticketCount ?? 0) - (a.ticketCount ?? 0);
      })
      .slice(0, POPULAR_LIMIT);
  }, [categories]);

  const favoriteCategories = useMemo(
    () => categories.filter((cat) => isFavorite('category', cat.id)),
    [categories, isFavorite],
  );
  const favoriteSubcategories = useMemo(() => {
    const out: { sub: Subcategory; cat: Category }[] = [];
    for (const cat of categories) {
      for (const sub of subcategoriesOf(cat)) {
        if (isFavorite('subcategory', sub.id)) out.push({ sub, cat });
      }
    }
    return out;
  }, [categories, isFavorite]);

  const hasFavorites = favoriteCategories.length > 0 || favoriteSubcategories.length > 0;


  const renderCategory = (cat: Category, highlight = '') => (
    <motion.div key={cat.id} variants={staggerItem}>
      <CatalogTile
        to={`/categories/${cat.id}/subcategories`}
        leading={<CategoryGlyph icon={cat.icon} seed={cat.id} />}
        title={<Highlight text={cat.name} query={highlight} />}
        meta={subcategoriesLabel(cat)}
        favorite={{ active: isFavorite('category', cat.id), onToggle: () => toggle('category', cat.id) }}
      />
    </motion.div>
  );

  const renderSubcategory = ({ sub, cat }: { sub: Subcategory; cat: Category }, highlight = '') => (
    <motion.div key={sub.id} variants={staggerItem}>
      <CatalogTile
        to={subcategoryPath(cat.id, sub.id)}
        leading={<CategoryGlyph icon={cat.icon} seed={cat.id} />}
        title={<Highlight text={sub.name} query={highlight} />}
        meta={cat.name}
        favorite={{ active: isFavorite('subcategory', sub.id), onToggle: () => toggle('subcategory', sub.id) }}
      />
    </motion.div>
  );

  const section = (title: string, count: number | null, items: React.ReactNode) => (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2>{title}</h2>
        {count !== null && <span className={styles.sectionCount}>{count}</span>}
      </div>
      <motion.div className={styles.grid} variants={gridStagger} initial="initial" animate="animate">
        {items}
      </motion.div>
    </section>
  );

  return (
    <PageMotion className={styles.wrapper}>
      <main>
        <h1 className={styles.pageTitle}>Каталог</h1>
        <p className={styles.subtitle}>Выберите категорию: внутри — инструкции, материалы и форма обращения</p>

        <SearchInput
          className={styles.searchBox}
          data-tour="catalog-search"
          label="Поиск по категориям и подкатегориям"
          value={query}
          onChange={setQuery}
        />

        {loading ? (
          <div className={styles.grid}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className={styles.skeleton} radius="var(--radius-lg)" />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <EmptyState
            Icon={CatalogIcon}
            title="Список услуг ещё не настроен"
            hint="Для вашей организации каталог пока пуст. Обратитесь в поддержку."
          />
        ) : searching ? (
          nothingFound ? (
            <EmptyState
              Icon={Search}
              title="Ничего не найдено"
              hint={`По запросу «${query.trim()}» нет ни категорий, ни подкатегорий. Попробуйте другое слово или выберите категорию из списка.`}              action={
                <Button variant="secondary" onClick={() => setQuery('')}>
                  Сбросить поиск
                </Button>
              }
            />
          ) : (
            <>
              {matchedCategories.length > 0 &&
                section('Категории', matchedCategories.length, matchedCategories.map((cat) => renderCategory(cat, q)))}
              {matchedSubcategories.length > 0 &&
                section(
                  'Подкатегории',
                  matchedSubcategories.length,
                  matchedSubcategories.map((item) => renderSubcategory(item, q)),
                )}
            </>
          )
        ) : (
          <>
            { }
            {popular.length > 0 && (
              <nav className={styles.popular} aria-label="Популярное">
                <span className={styles.popularLabel}>Популярное</span>
                {popular.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/categories/${cat.id}/subcategories`}
                    className={styles.chip}
                    title={cat.name}
                  >
                    <span className={styles.chipLabel}>{cat.name}</span>
                  </Link>
                ))}
              </nav>
            )}

            {hasFavorites &&
              section('Избранное', null, [
                ...favoriteCategories.map((cat) => renderCategory(cat)),
                ...favoriteSubcategories.map((item) => renderSubcategory(item)),
              ])}

            {section('Все категории', categories.length, categories.map((cat) => renderCategory(cat)))}
          </>
        )}
      </main>
    </PageMotion>
  );
};
